/**
 * MCP Server Create Page.
 *
 * Protocol-first form: only name + endpoint_url required.
 * Auth type, tier, and timeout are configurable.
 * Credentials are stored separately after creation.
 */

import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { formResolver } from '@/lib/form-resolver';
import { useI18n } from '@/i18n';
import {
  Button,
  Card,
  Icon,
  Input,
  Label,
  Select,
  Textarea,
} from '@/components/ui';
import { useCreateMCPServer, useMCPCatalogTemplate } from '@/features/mcp';
import apiClient from '@/lib/api-client';
import {
  mcpServerCreateSchema,
  defaultMCPServerFormValues,
  type MCPServerCreateForm,
} from '@/features/mcp/schemas';
import type { MCPServerCredentials } from '@/features/mcp/types';
import { toast } from 'sonner';

// =============================================================================
// Constants
// =============================================================================

const AUTH_TYPE_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: 'bearer', label: 'Bearer Token' },
  { value: 'api_key', label: 'API Key' },
  { value: 'oauth2_cc', label: 'OAuth2 Client Credentials' },
  { value: 'mtls', label: 'Mutual TLS (mTLS)' },
];

const TIER_OPTIONS = [
  { value: 'byos', label: 'BYOS (Bring Your Own Server)' },
  { value: 'platform_http', label: 'Platform HTTP' },
  { value: 'local_agent', label: 'Runner' },
];

// =============================================================================
// Component
// =============================================================================

export function MCPServerCreatePage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const createMutation = useCreateMCPServer();
  const [searchParams] = useSearchParams();
  const templateSlug = searchParams.get('template');

  // Fetch template data if creating from catalog
  const { data: template } = useMCPCatalogTemplate(templateSlug ?? '');

  const [credentials, setCredentials] = useState<MCPServerCredentials>({});

  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<MCPServerCreateForm>({
    resolver: formResolver(mcpServerCreateSchema),
    defaultValues: defaultMCPServerFormValues,
  });

  // Pre-fill form when template data loads
  useEffect(() => {
    if (template) {
      const dd = template.deployment_defaults;
      reset({
        ...defaultMCPServerFormValues,
        name: template.display_name,
        description: template.description ?? '',
        execution_tier: dd?.execution_tier === 'agent_managed' ? 'local_agent' : 'platform_http',
        auto_discover_tools: dd?.auto_discover_tools ?? false,
        auth_type: dd?.auth_type ?? 'none',
      });
      // Pre-fill OAuth2 token URL from template defaults
      if (dd?.oauth2_token_url_template) {
        setCredentials((prev) => ({
          ...prev,
          oauth2_token_url: dd.oauth2_token_url_template ?? '',
        }));
      }
    }
  }, [template, reset]);

  const authType = watch('auth_type');

  // ---------------------------------------------------------------------------
  // Submit
  // ---------------------------------------------------------------------------

  const onSubmit = async (data: MCPServerCreateForm) => {
    try {
      // 1. Create server
      const server = await createMutation.mutateAsync({
        name: data.name,
        description: data.description || undefined,
        endpoint_url: data.endpoint_url,
        auth_type: data.auth_type,
        execution_tier: data.execution_tier,
        timeout_seconds: data.timeout_seconds,
        auto_discover_tools: data.auto_discover_tools,
        ...(template ? { template_id: template.id } : {}),
      });

      // 2. Store credentials if auth type requires them
      const hasCredentials =
        credentials.bearer_token ||
        credentials.api_key ||
        credentials.oauth2_client_id;

      if (hasCredentials && server.id) {
        try {
          await apiClient.put(
            `/api/v1/mcp/servers/${server.id}/credentials`,
            credentials,
          );
        } catch {
          toast.warning(t('admin.integrations.credentialWarning'));
        }
      }

      toast.success(t('admin.integrations.created', { name: server.name }));
      navigate(`/integrations/${server.id}`);
    } catch (err) {
      toast.error(
        (err as Error).message || t('common.error')
      );
    }
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="p-6 max-w-3xl">
      {/* Header */}
      <div className="flex items-center gap-2 mb-6">
        <button
          onClick={() => navigate('/integrations')}
          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
        >
          <Icon name="arrow-left" size={20} />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {t('admin.integrations.createTitle')}
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('admin.integrations.createSubtitle')}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Template banner */}
        {template && (
          <Card className="p-4 bg-primary/5 border-primary/20">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                <Icon name="sparkles" size={16} className="text-primary" />
              </div>
              <div>
                <p className="text-sm font-medium">
                  Creating from template: {template.display_name}
                </p>
                <p className="text-xs text-muted-foreground">
                  {template.default_tools.length} tools, security policies pre-configured
                </p>
              </div>
            </div>
          </Card>
        )}

        {/* ===== Basic Info ===== */}
        <Card className="p-5 space-y-4">
          <h2 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
            {t('admin.integrations.basicInfo')}
          </h2>

          {/* Name */}
          <div>
            <Label htmlFor="name">{t('common.name')} *</Label>
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <Input
                  id="name"
                  placeholder="My MCP Server"
                  {...field}
                  error={errors.name?.message}
                />
              )}
            />
          </div>

          {/* Description */}
          <div>
            <Label htmlFor="description">{t('common.description')}</Label>
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <Textarea
                  id="description"
                  placeholder={t('admin.integrations.descriptionPlaceholder')}
                  rows={2}
                  {...field}
                />
              )}
            />
          </div>

          {/* Endpoint URL */}
          <div>
            <Label htmlFor="endpoint_url">{t('admin.integrations.endpointUrl')} *</Label>
            <Controller
              name="endpoint_url"
              control={control}
              render={({ field }) => (
                <Input
                  id="endpoint_url"
                  placeholder={
                    template?.deployment_defaults?.endpoint_url_template
                      ?? 'https://mcp.example.com/mcp'
                  }
                  {...field}
                  error={errors.endpoint_url?.message}
                />
              )}
            />
            {template?.deployment_defaults?.endpoint_url_template ? (
              <p className="text-xs text-gray-400 mt-1">
                Template URL pattern:{' '}
                <code className="bg-muted px-1 rounded text-[11px]">
                  {template.deployment_defaults.endpoint_url_template}
                </code>
                {' '}— replace the <code className="bg-muted px-1 rounded text-[11px]">{'{…}'}</code> placeholders with your values.
              </p>
            ) : (
              <p className="text-xs text-gray-400 mt-1">
                {t('admin.integrations.endpointUrlHint')}
              </p>
            )}
          </div>
        </Card>

        {/* ===== Connection Settings ===== */}
        <Card className="p-5 space-y-4">
          <h2 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
            {t('admin.integrations.connectionSettings')}
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Auth Type */}
            <div>
              <Label>{t('admin.integrations.authType')}</Label>
              <Controller
                name="auth_type"
                control={control}
                render={({ field }) => (
                  <Select
                    value={field.value}
                    onChange={field.onChange}
                    options={AUTH_TYPE_OPTIONS}
                  />
                )}
              />
            </div>

            {/* Execution Tier */}
            <div>
              <Label>{t('admin.integrations.tier')}</Label>
              <Controller
                name="execution_tier"
                control={control}
                render={({ field }) => (
                  <Select
                    value={field.value}
                    onChange={field.onChange}
                    options={TIER_OPTIONS}
                  />
                )}
              />
            </div>

            {/* Timeout */}
            <div>
              <Label>{t('admin.integrations.timeout')}</Label>
              <Controller
                name="timeout_seconds"
                control={control}
                render={({ field }) => (
                  <Input
                    type="number"
                    min={5}
                    max={300}
                    {...field}
                    onChange={(e) => field.onChange(Number(e.target.value))}
                    error={errors.timeout_seconds?.message}
                  />
                )}
              />
            </div>
          </div>
        </Card>

        {/* ===== Credentials (conditional) ===== */}
        {authType !== 'none' && (
          <Card className="p-5 space-y-4">
            <h2 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
              {t('admin.integrations.credentials')}
            </h2>
            <p className="text-xs text-gray-400">
              {t('admin.integrations.credentialsHint')}
            </p>

            {authType === 'bearer' && (
              <div>
                <Label>Bearer Token</Label>
                <Input
                  type="password"
                  placeholder="eyJhbGciOiJIUzI1NiIs..."
                  value={credentials.bearer_token || ''}
                  onChange={(e) =>
                    setCredentials((c) => ({ ...c, bearer_token: e.target.value }))
                  }
                />
              </div>
            )}

            {authType === 'api_key' && (
              <div>
                <Label>API Key</Label>
                <Input
                  type="password"
                  placeholder="sk-..."
                  value={credentials.api_key || ''}
                  onChange={(e) =>
                    setCredentials((c) => ({ ...c, api_key: e.target.value }))
                  }
                />
              </div>
            )}

            {authType === 'oauth2_cc' && (
              <div className="space-y-3">
                <div>
                  <Label>Client ID</Label>
                  <Input
                    placeholder="client-id"
                    value={credentials.oauth2_client_id || ''}
                    onChange={(e) =>
                      setCredentials((c) => ({ ...c, oauth2_client_id: e.target.value }))
                    }
                  />
                </div>
                <div>
                  <Label>Client Secret</Label>
                  <Input
                    type="password"
                    placeholder="client-secret"
                    value={credentials.oauth2_client_secret || ''}
                    onChange={(e) =>
                      setCredentials((c) => ({
                        ...c,
                        oauth2_client_secret: e.target.value,
                      }))
                    }
                  />
                </div>
                <div>
                  <Label>Token URL</Label>
                  <Input
                    placeholder={
                      template?.deployment_defaults?.oauth2_token_url_template
                        ?? 'https://auth.example.com/oauth2/token'
                    }
                    value={credentials.oauth2_token_url || ''}
                    onChange={(e) =>
                      setCredentials((c) => ({
                        ...c,
                        oauth2_token_url: e.target.value,
                      }))
                    }
                  />
                  {template?.deployment_defaults?.oauth2_token_url_template && (
                    <p className="text-xs text-gray-400 mt-1">
                      Hint: <code className="bg-muted px-1 rounded text-[11px]">
                        {template.deployment_defaults.oauth2_token_url_template}
                      </code>
                    </p>
                  )}
                </div>
              </div>
            )}
          </Card>
        )}

        {/* ===== Auto-discover toggle ===== */}
        <Card className="p-5">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium">
                {t('admin.integrations.autoDiscover')}
              </h3>
              <p className="text-xs text-gray-400 mt-0.5">
                {t('admin.integrations.autoDiscoverHint')}
              </p>
            </div>
            <Controller
              name="auto_discover_tools"
              control={control}
              render={({ field }) => (
                <input
                  type="checkbox"
                  checked={field.value}
                  onChange={field.onChange}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
              )}
            />
          </div>
        </Card>

        {/* ===== Actions ===== */}
        <div className="flex items-center justify-end gap-3 pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/integrations')}
          >
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? t('common.loading') : t('admin.integrations.addServer')}
          </Button>
        </div>
      </form>
    </div>
  );
}
